import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { toast } from 'react-hot-toast';

import LoadingOverlay from '../../components/LoadingOverlay';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    minCartValue: '',
    maxUses: '',
    maxUsesPerUser: '',
    startDate: '',
    expiryDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'coupons'));
      const couponsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by creation date (newest first)
      couponsData.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      setCoupons(couponsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to fetch coupons');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.value) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate start and expiry dates
    if (formData.startDate && formData.expiryDate) {
      const startDate = new Date(formData.startDate);
      const expiryDate = new Date(formData.expiryDate);

      if (startDate >= expiryDate) {
        toast.error('Start date must be before expiry date');
        return;
      }
    }

    try {
      setSaving(true);
      const couponData = {
        ...formData,
        code: formData.code.toUpperCase(),
        value: parseFloat(formData.value),
        minCartValue: formData.minCartValue ? parseFloat(formData.minCartValue) : 0,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        maxUsesPerUser: formData.maxUsesPerUser ? parseInt(formData.maxUsesPerUser) : null,
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : null,
        currentUses: 0,
        updatedAt: new Date()
      };

      if (editingCoupon) {
        await updateDoc(doc(db, 'coupons', editingCoupon.id), couponData);
        toast.success('Coupon updated successfully');
      } else {
        // Check if coupon code already exists
        const existingCoupon = coupons.find(c => c.code === couponData.code);
        if (existingCoupon) {
          toast.error('Coupon code already exists');
          return;
        }

        couponData.createdAt = new Date();
        await addDoc(collection(db, 'coupons'), couponData);
        toast.success('Coupon created successfully');
      }

      resetForm();
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      toast.error('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code || '',
      type: coupon.type || 'percentage',
      value: coupon.value || '',
      minCartValue: coupon.minCartValue || '',
      maxUses: coupon.maxUses || '',
      maxUsesPerUser: coupon.maxUsesPerUser || '',
      startDate: coupon.startDate ?
        new Date(coupon.startDate.seconds * 1000).toISOString().split('T')[0] : '',
      expiryDate: coupon.expiryDate ?
        new Date(coupon.expiryDate.seconds * 1000).toISOString().split('T')[0] : '',
      isActive: coupon.isActive !== undefined ? coupon.isActive : true
    });
    setShowModal(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      setDeletingId(couponId);
      await deleteDoc(doc(db, 'coupons', couponId));
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleCouponStatus = async (couponId, currentStatus) => {
    try {
      setTogglingId(couponId);
      await updateDoc(doc(db, 'coupons', couponId), {
        isActive: !currentStatus,
        updatedAt: new Date()
      });

      setCoupons(prev => prev.map(coupon =>
        coupon.id === couponId ? { ...coupon, isActive: !currentStatus } : coupon
      ));

      toast.success(`Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating coupon status:', error);
      toast.error('Failed to update coupon status');
    } finally {
      setTogglingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      value: '',
      minCartValue: '',
      maxUses: '',
      maxUsesPerUser: '',
      startDate: '',
      expiryDate: '',
      isActive: true
    });
    setEditingCoupon(null);
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date() > new Date(expiryDate.seconds * 1000);
  };

  const isNotStarted = (startDate) => {
    if (!startDate) return false;
    return new Date() < new Date(startDate.seconds * 1000);
  };

  const getStatusColor = (coupon) => {
    if (!coupon.isActive) return 'bg-gray-100 text-gray-800';
    if (isNotStarted(coupon.startDate)) return 'bg-blue-100 text-blue-800';
    if (isExpired(coupon.expiryDate)) return 'bg-red-100 text-red-800';
    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses) return 'bg-red-100 text-red-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (coupon) => {
    if (!coupon.isActive) return 'Inactive';
    if (isNotStarted(coupon.startDate)) return 'Not Started';
    if (isExpired(coupon.expiryDate)) return 'Expired';
    if (coupon.maxUses && coupon.currentUses >= coupon.maxUses) return 'Used Up';
    return 'Active';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8 md:pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif text-deep-brown">Coupons</h1>
            <p className="text-sm md:text-base text-warm-gray font-medium">Manage discount coupons and promotions</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-gold text-deep-brown px-4 md:px-6 py-2 rounded-md hover:bg-yellow-500 transition duration-200 font-medium text-sm md:text-base"
          >
            Create New Coupon
          </button>
        </div>



        {/* Coupons Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coupon Code
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Type & Value
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Usage
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Valid Period
                  </th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <div className="text-xs md:text-sm font-medium text-gray-900">{coupon.code}</div>
                      <div className="text-xs md:text-sm text-gray-500">
                        Min: ₹{coupon.minCartValue || 0}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="text-xs md:text-sm text-gray-900">
                        {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`} off
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-xs md:text-sm text-gray-900">
                        {coupon.currentUses || 0}
                        {coupon.maxUses ? ` / ${coupon.maxUses}` : ' / ∞'}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500">
                        Per user: {coupon.maxUsesPerUser || '∞'}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                      <span className={`inline-flex px-1.5 md:px-2 py-0.5 md:py-1 text-xs font-semibold rounded-full ${getStatusColor(coupon)}`}>
                        {getStatusText(coupon)}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-500 hidden md:table-cell">
                      <div>
                        {coupon.startDate ?
                          `From: ${new Date(coupon.startDate.seconds * 1000).toLocaleDateString()}` :
                          'From: Immediately'
                        }
                      </div>
                      <div>
                        {coupon.expiryDate ?
                          `To: ${new Date(coupon.expiryDate.seconds * 1000).toLocaleDateString()}` :
                          'To: Never'
                        }
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium">
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-60 text-left"
                          disabled={deletingId === coupon.id || togglingId === coupon.id}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleCouponStatus(coupon.id, coupon.isActive)}
                          className={`${coupon.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                            } disabled:opacity-60 text-left`}
                          disabled={deletingId === coupon.id}
                        >
                          {coupon.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="text-red-600 hover:text-red-900 disabled:opacity-60 text-left"
                          disabled={togglingId === coupon.id}
                        >
                          Delete
                        </button>
                      </div>
                      {(togglingId === coupon.id || deletingId === coupon.id) && (
                        <span className="mt-1 sm:ml-2 inline-block align-middle animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-2 border-amber-600 border-t-transparent" aria-label="Working" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {coupons.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No coupons found. Create your first coupon!</p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
              <LoadingOverlay show={saving} message={editingCoupon ? 'Updating coupon...' : 'Creating coupon...'} />
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={saving}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-60"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" aria-busy={saving}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coupon Code *
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      placeholder="e.g., SAVE20"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Type *
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      required
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value * {formData.type === 'percentage' ? '(%)' : '(₹)'}
                    </label>
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      min="0"
                      max={formData.type === 'percentage' ? '100' : undefined}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Cart Value (₹)
                    </label>
                    <input
                      type="number"
                      name="minCartValue"
                      value={formData.minCartValue}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Uses (Global)
                    </label>
                    <input
                      type="number"
                      name="maxUses"
                      value={formData.maxUses}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      min="1"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Uses Per User
                    </label>
                    <input
                      type="number"
                      name="maxUsesPerUser"
                      value={formData.maxUsesPerUser}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                      min="1"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to start immediately</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500 disabled:opacity-60"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for no expiry</p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={saving}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded disabled:opacity-60"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active (can be used by customers)
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-60"
                  >
                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;